import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { resolveShortUrl, resolveShortUrlFallback } from '../utils/urlShortener';
import logo from '../assets/img/logo.png';

/**
 * Extract the pathname from a URL string (handles both absolute and relative URLs).
 */
function extractPath(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname;
  } catch {
    return url;
  }
}

function ShortUrlResolver() {
  const { shortCode } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const resolveUrl = async () => {
      if (!shortCode) {
        setError(t('InvalidShortCode') || 'Invalid short code');
        setLoading(false);
        return;
      }

      const handleResolved = (url) => {
        const isViewerUrl = url.includes('/public/potree/') ||
                            url.includes('/public/cesium/');

        if (isViewerUrl) {
          // SPA navigation — no page reload, instant transition
          navigate(extractPath(url), { replace: true });
        } else {
          // Download links still need a real redirect
          setLoading(false);
          setDownloading(true);
          setTimeout(() => { window.location.href = url; }, 1500);
        }
      };

      try {
        // First try server-side resolution
        const result = await resolveShortUrl(shortCode);
        if (result.originalUrl) {
          handleResolved(result.originalUrl);
          return;
        }
      } catch (serverError) {
        console.warn('Server-side resolution failed, trying localStorage fallback:', serverError);

        // Try localStorage fallback
        try {
          const originalUrl = resolveShortUrlFallback(shortCode);
          if (originalUrl) {
            handleResolved(originalUrl);
            return;
          }
        } catch (fallbackError) {
          console.error('Fallback resolution also failed:', fallbackError);
        }
      }

      // If we reach here, both resolution methods failed
      setError(t('ShortUrlNotFound') || 'Short URL not found or has expired');
      setLoading(false);
    };

    resolveUrl();
  }, [shortCode, t, navigate]);

  if (downloading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2
        }}
      >
        <DownloadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
        <Typography variant="h6">
          {t('FileDownloadStarted') || 'Download Started'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('FileDownloadDescription') || 'Your file download should begin shortly. If it doesn\'t start automatically, please check your downloads folder.'}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          bgcolor: '#212529',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component='img'
          src={logo}
          alt='Mirukuru logo'
          sx={{ width: 300, mb: 3 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4 }}>
          <CircularProgress size={24} thickness={4} />
          <Typography variant='caption' sx={{ color: 'grey.400' }}>
            {t('ResolvingShortUrl') || 'Resolving short URL...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
          px: 3
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            {t('ShortUrlError') || 'Short URL Error'}
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary">
          {t('ShortUrlErrorDescription') || 'The short URL you requested could not be found or has expired.'}
        </Typography>
      </Box>
    );
  }

  return null;
}

export default ShortUrlResolver;